import {
  ApproversInterface,
  CreateLeaveRequestInterface, EditEmployeeLeavesInterface, EmployeeLeavesInterface,
  EmployeeYearPanelInterface, LeaveRequestInterface, LeaveRequestUpdateInterface,
  LeavesRepositoryInterface,
  LeaveTypeInterface, UsedLeaveTypesCountsInterface,
} from '@/services/Calendar/leaves/interfaces';
import { AxiosInstance } from 'axios';
import user from '@/Store/modules/user';

export class LeavesRepository implements LeavesRepositoryInterface {
  constructor(
    private axios: AxiosInstance,
  ) {
  }

  public async getUserApprovedLeaves(userId: string): Promise<LeaveRequestInterface[]> {
    const response = await this.axios.get(`/leave-calendar/requests/users/${userId}/approved`);

    return response.data;
  }

  public async getUserUpcomingLeaves(userId: string): Promise<LeaveRequestInterface[]> {
    const response = await this.axios.get(`/leave-calendar/requests/users/${userId}/upcoming`);

    return response.data;
  }

  public async getAllUserLeaves(userId: string, page: number): Promise<[LeaveRequestInterface[], number]> {
    const response = await this.axios.get(`/leave-calendar/requests/users/${userId}?page=${page}`);

    return response.data;
  }

  public async createRequest(data: CreateLeaveRequestInterface, document?: File): Promise<LeaveRequestInterface> {
    const formData = new FormData();
    if (document) {
      formData.append('document', document);
    }
    formData.append('requestBy', data.requestBy);
    formData.append('firstApprover', data.firstApprover);
    if (data.secondApprover) {
      formData.append('secondApprover', data.secondApprover);
    }
    formData.append('leaveTypeId', String(data.leaveTypeId));
    formData.append('dateFrom', data.dateFrom.toISOString());
    formData.append('dateTo', data.dateTo.toISOString());
    formData.append('days', String(data.days));
    formData.append('selectedDays', data.selectedDays);
    if (data.notes !== undefined && data.notes !== null) {
      formData.append('notes', data.notes);
    }

    const response = await this.axios.post('/leave-calendar/requests', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return response.data;
  }

  public async getAllLeaves(date?: string): Promise<LeaveRequestInterface[]> {
    const response = await this.axios.get(`/leave-calendar/requests?date=${date}`);

    return response.data;
  }

  public async getLeaveTypes(): Promise<LeaveTypeInterface[]> {
    const response = await this.axios.get('/leave-calendar/types');

    return response.data;
  }

  public async getPendingRequests(): Promise<LeaveRequestInterface[]> {
    const response = await this.axios.get('/leave-calendar/requests/pending');

    return response.data;
  }

  public async reviewRequest(requestId: number, isApproved: boolean): Promise<void> {
    await this.axios.patch(`/leave-calendar/requests/review/${requestId}`, { isApproved });
  }

  public async getAllApprovers(): Promise<ApproversInterface[]> {
    const response = await this.axios.get('/leave-calendar/approvers');

    return response.data;
  }

  public async getApproversByDepartment(departmentId: string): Promise<ApproversInterface> {
    const response = await this.axios.get(`/leave-calendar/approvers/${departmentId}`);

    return response.data;
  }

  public async saveApprovers(data: ApproversInterface): Promise<void> {
    await this.axios.post('/leave-calendar/approvers', data);
  }

  public async createCancellationRequest(requestId: number): Promise<void> {
    await this.axios.post('/leave-calendar/cancellations', { requestId });
  }

  public async reviewCancellationRequest(requestId: number, isApproved: boolean): Promise<void> {
    await this.axios.patch(`/leave-calendar/cancellations/review/${requestId}`, { isApproved });
  }

  public async getByUsersIds(usersIds: string[], date?: string): Promise<LeaveRequestInterface[]> {
    const data = !date ? { usersIds } : {
      usersIds,
      date,
    };

    const response = await this.axios.post('/leave-calendar/requests/users', data);

    return response.data;
  }

  public async getArchive(page: number, usersIds?: string[]): Promise<[LeaveRequestInterface[], number]> {
    const data = !usersIds ? { page } : {
      page,
      usersIds,
    };
    const response = await this.axios.post('/leave-calendar/requests/archive', data);

    return response.data;
  }

  public async getUsedLeaveTypesCounts(userId: string): Promise<UsedLeaveTypesCountsInterface[]> {
    const response = await this.axios.get(`leave-calendar/types/users/${userId}`);

    return response.data;
  }

  public async getEmployeeLeaves(userId: string): Promise<EmployeeLeavesInterface> {
    const response = await this.axios.get(`/leave-calendar/employee/${userId}`);

    return response.data;
  }

  public async getEmployeeLeavesByIds(userIds: string[]): Promise<EmployeeLeavesInterface[]> {
    const response = await this.axios.post('/leave-calendar/employee/ids', userIds);

    return response.data;
  }

  public async editEmployeeLeaves(userId: string, data: EditEmployeeLeavesInterface): Promise<void> {
    await this.axios.patch(`/leave-calendar/employee/${userId}`, data);
  }

  public async getEmployeeYearPanel(userId: string, year: number): Promise<EmployeeYearPanelInterface> {
    const response = await this.axios.get(`/leave-calendar/requests/${userId}/yearPanel/${year}`);

    return response.data;
  }

  public async updateLeaveRequest(
    requestId: number,
    updateDto: LeaveRequestUpdateInterface
  ): Promise<LeaveRequestInterface>{
    const response = await this.axios.put(`/leave-calendar/requests/${requestId}`, updateDto);

    return response.data;
  }

}
